// File: src/pages/HomePage.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { getAuth } from "firebase/auth";
import { fetchUserProfile } from "../services/auth";
import { useNavigate, Link } from "react-router-dom";
import { useProfile } from "../context/ProfileContext";
import { useMusic } from "../context/MusicContext";
import { motion, AnimatePresence } from "framer-motion";
import { FaPlay, FaChevronUp } from "react-icons/fa";

import TopMenuBar from "../layouts/TopMenuBar";
import HomeNewsFeed from "../components/home/HomeNewsFeed";
import PlatformFaq from "../components/home/PlatformFaq";
import HomeRadioPlayer from "../components/home/HomeRadioPlayer";

// 🖼️ Brand Assets
import localMusicLoudLight from "../assets/localMusicLoud.png";
import localMusicLoudDark from "../assets/localMusicLoud_darkmode.png";
import brandLoaderLogo from "../assets/Loader.png";
import atomicRadioHeader from "../assets/atomicRadioHeader.png"; // 👈 Add this asset import

const HomePage = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [creditBalance] = useState(100000); 
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const navigate = useNavigate();
  const auth = getAuth();
  const { user, isDarkMode } = useProfile();
  
  // 📻 Music Context & Expandable Radio State
  const { playSource, tuneInToRadio, setMuteExplicit } = useMusic();
  const [isRadioExpanded, setIsRadioExpanded] = useState(false);

  // Auto-expand if the radio is already currently playing
  useEffect(() => {
    if (playSource === "Radio") {
      setIsRadioExpanded(true);
    }
  }, [playSource]);

  const handleTuneIn = () => {
    setMuteExplicit(false);
    tuneInToRadio();
    setIsRadioExpanded(true);
  };

  // --- 🧭 DYNAMIC SCROLL COLLAPSE STATE FOR SUB-NAV ---
  const [isSubNavVisible, setIsSubNavVisible] = useState(true);
  const lastScrollY = useRef(0);
  const scrollContainerRef = useRef(null);

  // --- SECTION ANCHOR REFS FOR FAST LINKS ---
  const ethicalSectionRef = useRef(null);
  const syndicationSectionRef = useRef(null);
  const faqSectionRef = useRef(null);
  const policiesSectionRef = useRef(null);

  const scrollToSection = (elementRef) => {
    if (elementRef?.current) {
      elementRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleScroll = useCallback(() => {
    const currentScrollY =
      window.scrollY ||
      document.documentElement.scrollTop ||
      scrollContainerRef.current?.scrollTop ||
      0;

    if (currentScrollY <= 25) {
      setIsSubNavVisible(true);
      lastScrollY.current = 0;
      return;
    }

    if (currentScrollY > lastScrollY.current && currentScrollY > 60) {
      setIsSubNavVisible(false);
    } else if (currentScrollY < lastScrollY.current) {
      setIsSubNavVisible(true);
    }

    lastScrollY.current = currentScrollY;
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    window.addEventListener("scroll", handleScroll, { passive: true });
    if (container) {
      container.addEventListener("scroll", handleScroll, { passive: true });
    }
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  useEffect(() => {
    const redirectPath = sessionStorage.getItem("postAuthRedirect");
    if (redirectPath) {
      sessionStorage.removeItem("postAuthRedirect");
      navigate(redirectPath);
    }
  }, [navigate]);

  useEffect(() => {
    const fetchProfile = async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const profile = await fetchUserProfile(currentUser.uid);
        setUserProfile(profile);
      }
    };
    fetchProfile();
  }, [auth]);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const isSignedOut = !user || user.isAnonymous;

  return (
    <div
      ref={scrollContainerRef}
      className={`min-h-screen transition-colors duration-500 overflow-y-auto scroll-smooth ${
        isDarkMode
          ? "bg-brand-black text-white selection:bg-[#ff00cc]/30"
          : "bg-white text-slate-900 selection:bg-[#ff00cc]/20"
      }`}
    >
      {/* 📌 PERMANENT STATIC TOP MENU BAR */}
      <div className="fixed top-0 left-0 w-full z-50">
        <TopMenuBar userCredits={creditBalance} />
      </div>

      {/* 🧭 COLLAPSIBLE SUB NAVIGATION ANCHOR MENU */}
      <nav
        className={`fixed top-[60px] left-0 w-full z-40 border-b backdrop-blur-md px-6 py-3 transition-transform duration-300 ease-in-out ${
          isDarkMode ? "border-zinc-900 bg-brand-black" : "border-slate-200 bg-white shadow-xs"
        } ${
          isSubNavVisible ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <div
          className={`max-w-4xl mx-auto flex gap-x-6 gap-y-2 flex-wrap items-center justify-start text-[10px] font-black uppercase tracking-widest ${
            isDarkMode ? "text-zinc-400" : "text-slate-500"
          }`}
        >
          
          <button
            onClick={() => scrollToSection(ethicalSectionRef)}
            className="hover:text-brand-pink transition-colors focus:outline-none"
          >
            Culture
          </button>
          <button
            onClick={() => scrollToSection(syndicationSectionRef)}
            className="hover:text-brand-pink transition-colors focus:outline-none"
          >
            Radio
          </button>
          <button
            onClick={() => scrollToSection(faqSectionRef)}
            className="hover:text-brand-pink transition-colors focus:outline-none"
          >
            FAQ'S
          </button>
          <button
            onClick={() => scrollToSection(policiesSectionRef)}
            className="hover:text-brand-pink transition-colors focus:outline-none"
          >
            Policies
          </button>
        </div>
      </nav>

      {/* 🚀 HERO SPLASH SECTION */}
      <section
        ref={ethicalSectionRef}
        className="relative pt-36 sm:pt-40 pb-8 px-6 max-w-4xl mx-auto flex flex-col items-center justify-center text-center"
      >
        <div className="relative z-10 mb-3 animate-fade-in">
          <img
            src={brandLoaderLogo}
            alt="Atomic Brand Mark"
            className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-contain mx-auto select-none transition-transform duration-500 hover:scale-105"
            loading="eager"
          />
        </div>

        <h1 className="relative z-10 w-full flex justify-center items-center">
          <img
            src={isDarkMode ? localMusicLoudDark : localMusicLoudLight}
            alt="Local Music Loud"
            className="h-9 sm:h-12 md:h-16 lg:h-18 w-auto max-w-[85%] object-contain select-none"
            loading="eager"
          />
        </h1>

        {isSignedOut && (
          <div className="relative z-10 mt-6 flex flex-col sm:flex-row items-center justify-center gap-4 w-full max-w-xs sm:max-w-md mx-auto">
            <button
              onClick={() => navigate("/login", { state: { initialPopup: "login" } })}
              className={`w-full sm:w-auto px-8 py-3.5 text-xs font-black uppercase tracking-widest rounded-xl shadow-lg transition-all active:scale-95 text-center ${
                isDarkMode
                  ? "bg-brand-pink text-white hover:opacity-90 shadow-brand-pink/25"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              Login
            </button>

            <button
              onClick={() => navigate("/login", { state: { initialPopup: "signup" } })}
              className={`w-full sm:w-auto px-8 py-3.5 text-xs font-black uppercase tracking-widest rounded-xl transition-all active:scale-95 border-2 text-center ${
                isDarkMode
                  ? "border-brand-pink text-brand-pink bg-transparent hover:bg-brand-pink hover:text-white"
                  : "border-green-600 bg-green-600 text-white hover:bg-green-700"
              }`}
            >
              Sign Up
            </button>
          </div>
        )}
      </section>

      {/* 📰 NEWS & PRESS MOSAIC */}
      <HomeNewsFeed isDarkMode={isDarkMode} />

      {/* 📡 SYNDICATION NETWORK SECTION */}
      <section
        ref={syndicationSectionRef}
        className={`pt-6 pb-12 sm:pt-12 sm:pb-16 md:py-20 px-6 max-w-4xl mx-auto border-b text-left scroll-mt-28 transition-colors ${
          isDarkMode ? "border-zinc-900" : "border-slate-200"
        }`}
      >

        {/* 🌟 UNIFIED RADIO CARD CONTAINER (Tight Spacing & Zero-Gap Collapse) */}
        <div
          className={`w-full max-w-3xl mx-auto rounded-3xl border p-2 sm:p-3 overflow-hidden shadow-2xl backdrop-blur-md flex flex-col transition-colors ${
            isDarkMode ? "border-zinc-900 bg-zinc-950/60" : "border-slate-200 bg-slate-50"
          }`}
        >
          {/* 1. Map Viewport */}
          <div className="w-full aspect-[4/3] md:aspect-[16/10] rounded-2xl overflow-hidden shadow-inner">
            <iframe
              frameBorder="0"
              src="https://atomic-frontend.onrender.com/"
              title="Atomic Radio Map Network Sync"
              className={`w-full h-full ${isDarkMode ? "bg-black/20" : "bg-white"}`}
              style={{ borderStyle: "none" }}
            />
          </div>

          
          {/* 2. Atomic Radio Header (Shifted up into the vertical space) */}
          {!isRadioExpanded && (
            <div className="w-full flex justify-center items-center my-0">
              <motion.img
                layoutId="atomic-radio-header"
                src={atomicRadioHeader}
                alt="National Network Atomic Radio"
                className="h-14 sm:h-16 md:h-20 w-auto object-contain select-none -mt-4 sm:-mt-6 md:-mt-12"
                transition={{ type: "spring", stiffness: 350, damping: 30 }}
              />
            </div>
          )}

          {/* 3. "Tune In!" Pill Button / "Hide Player" Button (Tightened Vertical Margins) */}
          <div className={`w-full flex justify-center items-center ${isRadioExpanded ? "mt-2 mb-1" : "mt-5 mb-2"}`}>
            {!isRadioExpanded ? (
              <button
                onClick={handleTuneIn}
                className="group flex items-center gap-2.5 px-7 py-3 rounded-full bg-brand-pink text-white font-black uppercase tracking-widest text-xs shadow-lg shadow-brand-pink/30 hover:scale-105 active:scale-95 transition-all duration-200"
              >
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center">
                  <FaPlay className="text-[10px] ml-0.5 text-white" />
                </div>
                <span>Tune In!</span>
              </button>
            ) : (
              <button
                onClick={() => setIsRadioExpanded(false)}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-zinc-700/80 hover:border-zinc-500 bg-zinc-900/60 text-zinc-400 hover:text-white font-bold uppercase tracking-wider text-[10px] transition-all"
              >
                <FaChevronUp className="text-[9px]" />
                <span>Hide Player</span>
              </button>
            )}
          </div>

          {/* 4. Expandable Radio Player Drawer (No Excess Top Padding) */}
          <AnimatePresence>
            {isRadioExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.35, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="pt-1 pb-1">
                  <HomeRadioPlayer isRadioExpanded={isRadioExpanded} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ❓ ACCORDION FAQ SECTION */}
      <section ref={faqSectionRef} className="py-20 px-6 max-w-2xl mx-auto text-left scroll-mt-36">
        <header className="mb-12 text-center">
          <h2
            className={`text-2xl font-black italic tracking-tighter uppercase ${
              isDarkMode ? "text-white" : "text-slate-900"
            }`}
          >
            Frequently Asked Questions
          </h2>
          
        </header>

        <PlatformFaq isDarkMode={isDarkMode} />
      </section>

      {/* 🏢 LEGAL COMPLIANCE FOOTER GRID */}
      <footer
        ref={policiesSectionRef}
        className={`border-t py-12 px-6 mt-20 transition-colors duration-300 ${
          isDarkMode ? "border-zinc-900 bg-[#090909]" : "border-slate-200 bg-slate-50"
        }`}
      >
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-left">
            <h4
              className={`font-black italic text-sm tracking-tight uppercase ${
                isDarkMode ? "text-white" : "text-slate-900"
              }`}
            >
              Atomic Media
            </h4>
            <p
              className={`text-[10px] font-bold uppercase tracking-widest mt-0.5 ${
                isDarkMode ? "text-zinc-600 dark:text-zinc-500" : "text-slate-400"
              }`}
            >
              Local Music Loud
            </p>
          </div>

          <nav
            className={`flex flex-wrap gap-x-6 gap-y-2 text-[11px] font-black uppercase tracking-wider ${
              isDarkMode ? "text-zinc-400" : "text-slate-500"
            }`}
          >
            <Link to="/terms" className="hover:text-brand-pink transition-colors">
              Terms &amp; Conditions
            </Link>
            <Link to="/copyright" className="hover:text-brand-pink transition-colors">
              Copyright Policy
            </Link>
            <Link to="/privacy" className="hover:text-brand-pink transition-colors">
              Privacy Policy
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;